import { useMemo } from 'react';

import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import { PATHS } from 'routes/routes.constants';

import { usePathname, useRouter } from '@polyai/common/hooks/useRouter';
import type { ComponentWithChildren } from '@polyai/common/types/helpers';

import { isOpenTableUser } from 'screens/OTLogin/utils';

const OT_PATHS: string[] = [
  PATHS.OPENTABLE_REGION,
  PATHS.OPENTABLE_CALLBACK,
  PATHS.OPENTABLE_LOGIN,
  PATHS.OPENTABLE_WORKSPACE,
  PATHS.OPENTABLE_NEW_AGENT,
];

export const Auth0Wrapper: ComponentWithChildren = ({ children }) => {
  const { replaceUrl } = useRouter();

  const onRedirectCallback = (appState?: AppState) => {
    replaceUrl(appState?.returnTo?.replace(window.location.origin, '') || '/');
  };

  const pathname = usePathname();

  // Determine if we should skip the Auth0 redirect callback based on whether we're
  // on an OpenTable-related path or if the user is an OpenTable user.
  // This prevents unnecessary redirects for OpenTable users and ensures
  // they are directed to the correct Auth0 tenant.
  const [shouldSkipRedirect, isOTUser] = useMemo(() => {
    const isOpenTablePath = OT_PATHS.includes(pathname);
    return [isOpenTablePath, isOpenTablePath || isOpenTableUser()] as const;
  }, [pathname]);

  const domain =
    (isOTUser && process.env.NEXT_PUBLIC_AUTH0_STUDIO_DOMAIN) ||
    process.env.NEXT_PUBLIC_AUTH0_DOMAIN!;

  return (
    <Auth0Provider
      authorizationParams={{
        audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE!,
        redirect_uri: `${window.location.origin}${PATHS.CALLBACK}`,
      }}
      cacheLocation="localstorage"
      clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID!}
      domain={domain}
      skipRedirectCallback={shouldSkipRedirect}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};
